import { createContext, useReducer } from 'react';
import { StreamChat } from 'stream-chat';
import { chatReducer } from './reducer';
import { createClient, extractUnreadCount, setChatUser } from 'src/utils/streamChat';

type ContextState = {
  chatClient: StreamChat | undefined;
  unreadCount: number;
  isInitializingChat: boolean;
  chatDispatch: (action: ChatDispatchWrapperActionType) => Promise<void>;
};

export const ChatContext = createContext<ContextState>({} as ContextState);

type ChatDispatchWrapperActionType = {
  type: 'initializeChatClient';
  payload: {
    token: string;
    user: { id: string; name?: string; image?: string };
  };
};

export const ChatContextProvider: React.FC = ({ children }) => {
  const [chatState, chatDispatch] = useReducer(chatReducer, {
    chatClient: undefined,
    unreadCount: 0,
    isInitializingChat: false,
  });

  const wrapperDispatch = async (action: ChatDispatchWrapperActionType) => {
    switch (action.type) {
      case 'initializeChatClient': {
        if (chatState.isInitializingChat || chatState.chatClient) {
          return;
        }
        const chatClient = createClient(process.env.NEXT_PUBLIC_STREAM_API_KEY || '');
        const { token, user } = action.payload;
        chatDispatch({ type: 'beginChatClientInitialization' });
        const setUserRes = await setChatUser(token, user, chatClient);

        chatClient.on((event) => {
          if (typeof event.total_unread_count === 'number') {
            chatDispatch({ type: 'updateUnreadCount', payload: { unreadCount: event.total_unread_count } });
          }
        });
        chatDispatch({
          type: 'completeChatClientInitialization',
          payload: { chatClient, unreadCount: extractUnreadCount(setUserRes) },
        });
        break;
      }
    }
  };

  return (
    <ChatContext.Provider
      value={{
        ...chatState,
        chatDispatch: wrapperDispatch,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
